import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { HttpStatusCode } from '@commutifi-fe/constants';
import { useIsMountedRef } from '@commutifi-fe/hooks';
import { ApiError } from '@commutifi-fe/interfaces';
import { logger } from '@commutifi-fe/logger';
import { STAY_LOGGED_IN_STORAGE_ID } from 'constants/settings';
import { SSO_LANDING } from 'api/modules/authentication/api';
import { parse, stripSearchParam } from 'utils/queryParams';
import { getAuthenticatedAccountId } from 'api/index';
import { wrapActionInPromise } from 'utils/helpers';
import { fetchMe } from 'api/modules/accounts/api';
import { authActions } from 'store/modules/auth';
import { useAppDispatch } from 'store/index';
import localStorage from 'utils/storage';
const hasSsoLandingMarker = (search: string): boolean => parse<Record<string, string>>(search)?.[SSO_LANDING.param] === SSO_LANDING.marker;

// The whole app is held behind this probe's loader (see app.tsx), so it must be bounded
// independently of the shared axios client's timeout, which is measured in minutes and is far too
// long to gate a page load on. On timeout we fall through to anonymous exactly like a 401.
// Exported so tests advance by the real bound rather than a copy of it.
export const HYDRATION_PROBE_TIMEOUT_MS = 8000;
class SsoHydrationTimeoutError extends Error {
  constructor() {
    super('SSO landing hydration probe timed out');
    this.name = 'SsoHydrationTimeoutError';
  }
}

/**
 * Races `run` against the bound and aborts it when the bound wins, so the timeout is terminal.
 * Racing alone would only release the loader: the request would keep going and a late success could
 * still dispatch `loginSuccess`, pulling the user off the login card seconds after they landed on
 * it. Aborting is what makes "falls through to anonymous exactly like a 401" true.
 */
const withTimeout = <T,>(run: (signal: AbortSignal) => Promise<T>, ms: number): Promise<T> => {
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([run(controller.signal), new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort();
      reject(new SsoHydrationTimeoutError());
    }, ms);
  })]).finally(() => {
    clearTimeout(timeoutId);
  });
};

/**
 * Closes the last mile of the WorkOS org-scoped SSO flow. The dashboard establishes the session
 * cookie server-side and redirects back with `?sso=1`, but because that redirect is not an XHR
 * nothing writes the SPA's `account:id` login gate, so the app would re-render the login card over a
 * valid session. On landing we read the established session once via `fetchMe` (the session cookie
 * rides along cross-port) and, when it resolves an account, dispatch `loginSuccess` — the same
 * action every password/OTP/OAuth login ends on, which writes `account:id` and flips
 * `auth.loggedIn`.
 *
 * The probe is gated two ways: no marker means a genuine anonymous visit, and an existing
 * `account:id` short-circuits so we never fire a doomed `fetchMe` (→ 401) on a normal page load.
 * The marker is stripped on every path that finds one, including those short-circuits, so a reload
 * or a shared link cannot re-enter the probe.
 *
 * Note what that second gate does *not* guarantee: it only knows an id is present, not that it
 * belongs to the session the cookie now carries. Signing in as B from a tab still holding A's id
 * skips the probe and leaves the gate on A. Displayed identity stays correct — that reads the
 * session through `CurrentAccountProvider` — but the stored id, which `interceptors.hasToken()`
 * and the initial `auth.loggedIn` both read, drifts until the tab is reloaded.
 *
 * Returns whether hydration is still in flight so the caller can hold a loader instead of flashing
 * the login card before the redirect into the app.
 */
export function useSsoLandingHydration(): boolean {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const isMountedRef = useIsMountedRef();
  const hasRunRef = useRef(false);
  const [isHydrating, setIsHydrating] = useState(() => !getAuthenticatedAccountId() && hasSsoLandingMarker(history.location.search));
  useEffect(() => {
    if (hasRunRef.current) {
      return;
    }
    hasRunRef.current = true;

    // Read the location off `history` rather than `useLocation()` throughout: this runs once on
    // landing, and subscribing to the router here would re-render the whole app on every later
    // navigation.
    const stripMarker = () => {
      const {
        pathname,
        search,
        hash,
        state
      } = history.location;
      const nextSearch = stripSearchParam(search, SSO_LANDING.param);
      if (nextSearch === search) {
        return;
      }
      history.replace({
        pathname,
        search: nextSearch,
        hash,
        state
      });
    };
    if (!isHydrating) {
      stripMarker();
      return;
    }
    async function establishSession(signal: AbortSignal) {
      const {
        account
      } = await fetchMe({
        signal
      });
      if (account?.id && isMountedRef.current) {
        await wrapActionInPromise((...args: Parameters<typeof authActions.loginSuccess>) => dispatch(authActions.loginSuccess(...args)), account, {
          stayLoggedIn: localStorage.get(STAY_LOGGED_IN_STORAGE_ID)
        });
      }
    }
    async function hydrate() {
      try {
        // Bound the whole sequence, not just the request: `loginSuccess` resolves through a
        // `takeLatest` saga, so a cancelled task would leave that await pending forever and strand
        // the app on the loader this hook gates.
        await withTimeout(establishSession, HYDRATION_PROBE_TIMEOUT_MS);
      } catch (error) {
        // A 401 is the expected anonymous outcome — there is no session to adopt, e.g. someone
        // opened `?sso=1` by hand. Anything else (timeout, 5xx, network) happened during what
        // should have been a completed WorkOS redirect, so it has to surface.
        const status = ApiError.isApiError(error) ? error.status : undefined;
        // A timeout that still left us authenticated means `loginSuccess` landed and only its
        // saga acknowledgement was lost (a cancelled `takeLatest` never settles its promise).
        // The session is established, so reporting it would be noise, not signal.
        const sessionEstablished = Boolean(getAuthenticatedAccountId());
        if (status !== HttpStatusCode.Unauthorized && !sessionEstablished) {
          logger.captureWarning('SSO landing hydration failed unexpectedly', {
            name: error instanceof Error ? error.name : 'unknown',
            message: error instanceof Error ? error.message : String(error),
            status
          });
        }
      } finally {
        if (isMountedRef.current) {
          // Release the loader before touching history: `replace` can throw (browsers rate-limit
          // replaceState), and doing it the other way round would strand the whole app on the
          // loader this hook gates with no way out.
          setIsHydrating(false);
          stripMarker();
        }
      }
    }
    void hydrate();
  }, [isHydrating, history, dispatch, isMountedRef]);
  return isHydrating;
}